import React, { useState, useEffect } from "react";
import {
  Save,
  Send,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Clock,
  Paperclip,
  X,
  FileText,
} from "lucide-react";
import InspectionChecklist, { ChecklistResponse } from "./InspectionChecklist";
import DynamicAssetFields from "./DynamicAssetFields";
import { useAuth } from "../../auth/AuthProvider";
import { useAssetInspectionStore } from "../../store/assetInspectionStore";
import { AssetChecklist } from "../../service/assetInspectionService";
import axiosInstance from "../../common/axiosInstance";
import { useS3Upload, S3TempUrl } from "../../utils/s3Client";

interface InspectionFormProps {
  asset: {
    id: string;
    equipmentInventory: string; // Equipment category ID
    equipmentInventoryName?: string; // Equipment category name (for display)
    equipmentSubcategory?: string; // Subcategory ID
    equipmentSubcategoryName?: string; // Subcategory name (for display)
    location: string;
    floor: string;
    type?: string; // Asset type (e.g., "CO2", "DCP")
    HB_No?: string;
  };
  onCancel: () => void;
  onSubmit?: (inspectionData: InspectionSubmissionData) => Promise<void>;
  onSaveDraft?: (inspectionData: InspectionSubmissionData) => Promise<void>;
  initialData?: InspectionSubmissionData;
}

export interface InspectionSubmissionData {
  assetId: string;
  inspectorName: string;
  inspectorEmail: string;
  inspectionDate: string;
  inspectionType: "routine" | "maintenance" | "emergency" | "compliance";
  responses: ChecklistResponse[];
  overallStatus: "pass" | "fail" | "attention";
  overallComments: string;
  correctiveActions?: string;
  nextInspectionDate: string; // Now required
  // Asset-specific fields (using existing DB field names)
  hptTestDate?: string;
  nextServiceDueDate?: string;
  lastRefillDate?: string;
  nextRefillDueDate?: string;
  amcStatus?: "active" | "inactive" | "expired" | "pending" | "not-applicable";
  amcStartDate?: string;
  amcEndDate?: string;
  amcVendor?: string;
  toBeReplaced?: boolean;
  attachments?: string[]; // S3 URLs for attachments
  isDraft: boolean;
  HB_No?: string;
  type?: string;
  description?: string;
  location?: string;
  qty?: string;
  floor?: string;
}

const InspectionForm: React.FC<InspectionFormProps> = ({
  asset,
  onSubmit,
  onCancel,
  onSaveDraft,
  initialData,
}) => {
  const { user } = useAuth();
  const { suggestAssetChecklists, createAssessment } =
    useAssetInspectionStore();
  const { uploadFileToS3, uploading } = useS3Upload();
  const { getsignedurl } = S3TempUrl();

  const [formData, setFormData] = useState<InspectionSubmissionData>({
    assetId: asset.id,
    inspectorName: initialData?.inspectorName || "",
    inspectorEmail: initialData?.inspectorEmail || "",
    attachments: initialData?.attachments || [],
    inspectionDate:
      initialData?.inspectionDate || new Date().toISOString().split("T")[0],
    inspectionType: initialData?.inspectionType || "routine",
    responses: initialData?.responses || [],
    overallStatus: initialData?.overallStatus || "attention",
    overallComments: initialData?.overallComments || "",
    correctiveActions: initialData?.correctiveActions || "",
    nextInspectionDate: initialData?.nextInspectionDate || "",
    // Asset-specific fields (will be pre-filled from asset data)
    hptTestDate: initialData?.hptTestDate || '',
    nextServiceDueDate: initialData?.nextServiceDueDate || '',
    lastRefillDate: initialData?.lastRefillDate || '',
    nextRefillDueDate: initialData?.nextRefillDueDate || '',
    amcStatus: initialData?.amcStatus || 'not-applicable',
    amcStartDate: initialData?.amcStartDate || '',
    amcEndDate: initialData?.amcEndDate || '',
    amcVendor: initialData?.amcVendor || '',
    toBeReplaced: initialData?.toBeReplaced || false,
    isDraft: initialData?.isDraft || false,
    HB_No: asset?.HB_No || '',
    type: initialData?.type || '',
    description: initialData?.description || '',
    location: initialData?.location || '',
    qty: initialData?.qty || '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [currentChecklist, setCurrentChecklist] =
    useState<AssetChecklist | null>(null);
  const [assetDataLoaded, setAssetDataLoaded] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);

  // Helper function to convert ISO date to YYYY-MM-DD format
  const formatDateForInput = (
    dateString: string | null | undefined
  ): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      // Format as YYYY-MM-DD for date input
      return date.toISOString().split("T")[0];
    } catch (error) {
      return "";
    }
  };

  // Check if equipment is Fire Extinguisher
  const isFireExtinguisher = () => {
    const equipmentName = asset.equipmentInventoryName?.toLowerCase() || "";
    return equipmentName.includes("extinguisher");
  };

  // Fetch asset data and pre-fill fields
  useEffect(() => {
    const fetchAssetData = async () => {
      if (assetDataLoaded) return;

      try {
        const response = await axiosInstance.get(`/api/assets/${asset.id}`);

        if (response.data.success) {
          const assetData = response.data.data || response.data.asset;

          // Pre-fill common field (toBeReplaced) for all asset types
          setFormData((prev) => ({
            ...prev,
            toBeReplaced: assetData.toBeReplaced || false,
            // Pre-fill fire-extinguisher-specific fields if applicable
            ...(isFireExtinguisher()
              ? {
                  cylinderNumber: assetData.cylinderNumber || "",
                  capacity: assetData.capacity || "",
                  type: assetData.type || "",
                  floor: assetData.floor || "",
                  location: assetData.location || "",
                  hptTestDate: formatDateForInput(assetData.hptTestDate),
                  nextServiceDueDate: formatDateForInput(assetData.nextServiceDueDate),
                  lastRefillDate: formatDateForInput(assetData.lastRefillDate),
                  nextRefillDueDate: formatDateForInput(assetData.nextRefillDueDate),
                  amcStatus: assetData.amcStatus || prev.amcStatus,
                  amcStartDate: formatDateForInput(assetData.amcStartDate),
                  amcEndDate: formatDateForInput(assetData.amcEndDate),
                  amcVendor: assetData.amcVendor || prev.amcVendor,
                  HB_No: assetData.HB_No || prev.HB_No,
                  description: assetData.description || prev.description,
                  qty: assetData.qty || prev.qty,
                }
              : {}),
          }));

          setAssetDataLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching asset data:", error);
        setAssetDataLoaded(true);
      }
    };

    fetchAssetData();
  }, [asset.id, assetDataLoaded]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleAttachmentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    try {
      setAttachmentsError(null);
      const uploadedUrls = await Promise.all(
        Array.from(files).map((file) => uploadFileToS3(file))
      );
      setFormData((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...uploadedUrls],
      }));
    } catch (e: any) {
      setAttachmentsError(e?.message || "Failed to upload attachments");
    }
  };

  const removeAttachment = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, i) => i !== index),
    }));
  };

  const getFileNameFromUrl = (url: string): string => {
    try {
      const withoutQuery = url.split("?")[0];
      return decodeURIComponent(
        withoutQuery.substring(withoutQuery.lastIndexOf("/") + 1)
      );
    } catch {
      return url;
    }
  };

  const handleViewFile = async (url: string) => {
    try {
      const key = url.split("amazonaws.com/")[1] || getFileNameFromUrl(url);
      const signed = await getsignedurl(key, "application/octet-stream");
      window.open(signed, "_blank");
    } catch {
      window.open(url, "_blank");
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.inspectorName.trim()) {
      newErrors.inspectorName = "Inspector name is required";
    }

    if (!formData.inspectorEmail.trim()) {
      newErrors.inspectorEmail = "Inspector email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.inspectorEmail)) {
      newErrors.inspectorEmail = "Please enter a valid email address";
    }

    if (!formData.inspectionDate) {
      newErrors.inspectionDate = "Inspection date is required";
    }

    if (!formData.nextInspectionDate) {
      newErrors.nextInspectionDate = "Next inspection date is required";
    }

    // Check if all required checklist items are completed
    const requiredResponses = formData.responses.filter((response) => {
      // If status is "na", it's a valid response (Not Applicable)
      if (response.status?.toLowerCase() === "na") {
        return false;
      }
      return (
        response.value === "" ||
        response.value === null ||
        response.value === undefined
      );
    });

    if (requiredResponses.length > 0 && !formData.isDraft) {
      newErrors.responses = "Please complete all required checklist items";
    }

    // Validate comments for non-compliant checklist items
    const missingComments = formData.responses.filter((r) => {
      const isNonCompliant =
        r.value === 'not_complied' || r.value === 'partial_complied' || r.value === false;
      return isNonCompliant && !r.comment?.trim();
    });

    if (missingComments.length > 0 && !formData.isDraft) {
      newErrors.responses = `Comments are mandatory for ${missingComments.length} non-compliant item(s). Please add remarks.`;
    }

    if (!formData.overallComments.trim() && formData.overallStatus === "fail") {
      newErrors.overallComments =
        "Comments are required when marking inspection as failed";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const calculateOverallStatus = (
    responses: ChecklistResponse[]
  ): "pass" | "fail" | "attention" => {
    if (responses.length === 0) return "attention";

    const hasFailures = responses.some(
      (response) => response.status === "fail"
    );
    if (hasFailures) return "fail";

    const hasAttention = responses.some(
      (response) => response.status === "attention"
    );
    if (hasAttention) return "attention";

    return "pass";
  };

  const handleResponsesChange = (responses: ChecklistResponse[]) => {
    const overallStatus = calculateOverallStatus(responses);
    setFormData((prev) => ({
      ...prev,
      responses,
      overallStatus,
    }));
  };

  const handleSubmit = async (isDraft: boolean = false) => {
    const submissionData = { ...formData, isDraft };

    if (!isDraft && !validateForm()) {
      return;
    }

    if (!currentChecklist) {
      alert(
        "No checklist loaded. Please wait for the checklist to load or select one manually."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = user?.id || (user as any)?._id || "current-user";

      const assessmentData: any = {
        assetId: asset.id,
        assetChecklistId: currentChecklist._id || currentChecklist.id || "",
        inspectorName: formData.inspectorName,
        inspectorEmail: formData.inspectorEmail,
        inspectorId: userId,
        inspectionDate: formData.inspectionDate,
        inspectionType: formData.inspectionType,
        responses: formData.responses,
        overallStatus: formData.overallStatus,
        overallComments: formData.overallComments,
        correctiveActions: formData.correctiveActions,
        nextInspectionDate: formData.nextInspectionDate,
        attachments: formData.attachments || [],
        // Asset-specific fields (using existing DB field names)
        hptTestDate: formData.hptTestDate || undefined,
        nextServiceDueDate: formData.nextServiceDueDate || undefined,
        lastRefillDate: formData.lastRefillDate || undefined,
        nextRefillDueDate: formData.nextRefillDueDate || undefined,
        amcStatus: formData.amcStatus || undefined,
        amcStartDate: formData.amcStartDate || undefined,
        amcEndDate: formData.amcEndDate || undefined,
        amcVendor: formData.amcVendor || undefined,
        HB_No: formData.HB_No || undefined,
        isDraft,
        createdBy: userId,
      };
      await createAssessment(assessmentData);

      // Update the asset's toBeReplaced flag
      if (!isDraft) {
        try {
          await axiosInstance.put(`/api/assets/${asset.id}`, {
            toBeReplaced: formData.toBeReplaced || false,
          });
        } catch (err) {
          console.error("Error updating asset replacement status:", err);
        }
      }

      alert(
        isDraft
          ? "Draft saved successfully!"
          : "Inspection submitted successfully!"
      );
      onCancel();
    } catch (error: any) {
      console.error("Error submitting inspection:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "Failed to submit inspection. Please try again.";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pass":
        return <CheckCircle size={20} className="text-green-500" />;
      case "fail":
        return <AlertTriangle size={20} className="text-red-500" />;
      default:
        return <Clock size={20} className="text-orange-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pass":
        return "bg-green-50 border-green-200 text-green-800";
      case "fail":
        return "bg-red-50 border-red-200 text-red-800";
      default:
        return "bg-orange-50 border-orange-200 text-orange-800";
    }
  };

  const completionPercentage =
    formData.responses.length > 0
      ? Math.round(
          (formData.responses.filter(
            (r) =>
              // Consider response complete if it has a value OR if status is "na" (Not Applicable)
              r.status?.toLowerCase() === "na" ||
              (r.value !== "" && r.value !== null && r.value !== undefined)
          ).length /
            formData.responses.length) *
            100
        )
      : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Asset Details</span>
            </button>

            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                Completion: {completionPercentage}%
              </div>
              <div
                className={`px-3 py-1 rounded-full border text-sm font-medium flex items-center gap-1 ${getStatusColor(
                  formData.overallStatus
                )}`}
              >
                {getStatusIcon(formData.overallStatus)}
                <span className="capitalize">{formData.overallStatus}</span>
              </div>
            </div>
          </div>

          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Asset Inspection
            </h1>
            <p className="text-gray-600 mt-1">
              {asset.equipmentInventoryName || asset.equipmentInventory}
              {/* {asset.equipmentSubcategoryName && ` • ${asset.equipmentSubcategoryName}`} */}
              {asset.type && ` • ${asset.type}`}
              {asset.location && ` • ${asset.location}`}
              {asset.floor && ` • Floor ${asset.floor}`}
            </p>
          </div>
        </div>

        {/* Inspector Information */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Inspector Information
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspector Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.inspectorName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    inspectorName: e.target.value,
                  }))
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  errors.inspectorName ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Enter inspector name"
              />
              {errors.inspectorName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.inspectorName}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspector Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.inspectorEmail}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    inspectorEmail: e.target.value,
                  }))
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  errors.inspectorEmail ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Enter inspector email"
              />
              {errors.inspectorEmail && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.inspectorEmail}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspection Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.inspectionDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    inspectionDate: e.target.value,
                  }))
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  errors.inspectionDate ? "border-red-300" : "border-gray-300"
                }`}
              />
              {errors.inspectionDate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.inspectionDate}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspection Type
              </label>
              <select
                value={formData.inspectionType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    inspectionType: e.target.value as any,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="routine">Routine Inspection</option>
                <option value="maintenance">Maintenance Check</option>
                <option value="emergency">Emergency Inspection</option>
                <option value="compliance">Compliance Audit</option>
              </select>
            </div>
          </div>
        </div>

        {/* Inspection Checklist */}
        <InspectionChecklist
          equipmentInventoryId={asset.equipmentInventory}
          equipmentSubCategoryId={asset.equipmentSubcategory}
          assetType={asset.type}
          onResponsesChange={handleResponsesChange}
          initialResponses={formData.responses}
          onChecklistChange={(checklist) => {
            setCurrentChecklist(checklist);
          }}
        />

        {/* Asset-Specific Information (Only for Fire Extinguishers for now) */}
        <DynamicAssetFields
          // formData={formData}
          values={formData}
          onChange={handleFieldChange}
          errors={errors}
          title="Asset-Specific Information"
          disabled={false}
          className="w-full border border-gray-300 rounded-lg px-3 py-2"
          equipmentInventoryName={asset.equipmentInventoryName}
        />

        {/* Attachments */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Attachments
          </h2>

          <div className="space-y-4">
            {/* File Upload Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Supporting Documents
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  id="attachments"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.txt"
                  onChange={handleAttachmentUpload}
                  className="hidden"
                />
                <label
                  htmlFor="attachments"
                  className="cursor-pointer flex flex-col items-center gap-2"
                >
                  <Paperclip size={32} className="text-gray-400" />
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-blue-600">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </div>
                  <div className="text-xs text-gray-500">
                    PDF, DOC, DOCX, JPG, PNG, XLSX up to 10MB each
                  </div>
                </label>
              </div>
              {attachmentsError && (
                <p className="text-red-500 text-sm mt-2">{attachmentsError}</p>
              )}
              {uploading && (
                <p className="text-gray-500 text-sm mt-2">Uploading...</p>
              )}
            </div>

            {/* Attached Files List */}
            {(formData.attachments?.length || 0) > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attached Files ({formData.attachments?.length || 0})
                </label>
                <div className="space-y-2">
                  {(formData.attachments || []).map((url, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">
                            {getFileNameFromUrl(url)}
                          </p>
                          <button
                            type="button"
                            onClick={() => handleViewFile(url)}
                            className="text-xs text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove file"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Overall Assessment */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">
            Overall Assessment
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overall Comments {formData.overallStatus === "fail" && "*"}
              </label>
              <textarea
                value={formData.overallComments}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    overallComments: e.target.value,
                  }))
                }
                className={`w-full border rounded-lg px-3 py-2 ${
                  errors.overallComments ? "border-red-300" : "border-gray-300"
                }`}
                rows={4}
                placeholder="Provide overall assessment and any additional observations..."
              />
              {errors.overallComments && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.overallComments}
                </p>
              )}
            </div>

            {formData.overallStatus === "fail" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Corrective Actions Required
                </label>
                <textarea
                  value={formData.correctiveActions}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      correctiveActions: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows={3}
                  placeholder="Describe required corrective actions..."
                />
              </div>
            )}

            {/* To Be Replaced toggle */}
            <div className={`flex items-center gap-4 p-3 rounded-lg border-2 ${
              formData.toBeReplaced
                ? 'border-red-400 bg-red-50'
                : 'border-amber-400 bg-amber-50'
            }`}>
              <label className="text-sm font-bold text-amber-900">
                Asset needs to be replaced?
              </label>
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    toBeReplaced: !prev.toBeReplaced,
                  }))
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.toBeReplaced ? "bg-red-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.toBeReplaced ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <span
                className={`text-sm font-semibold ${
                  formData.toBeReplaced ? "text-red-600" : "text-gray-500"
                }`}
              >
                {formData.toBeReplaced ? "Yes" : "No"}
              </span>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Next Inspection Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.nextInspectionDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    nextInspectionDate: e.target.value,
                  }))
                }
                className={`w-full border rounded-lg px-3 py-2 max-w-xs ${
                  errors.nextInspectionDate
                    ? "border-red-300"
                    : "border-gray-300"
                }`}
              />
              {errors.nextInspectionDate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.nextInspectionDate}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Note: New inspections cannot be created before this date
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <div className="flex gap-3">
              {onSaveDraft && (
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Save size={20} />
                  Save as Draft
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={() => handleSubmit(false)}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-3 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors disabled:opacity-50"
              >
                <Send size={20} />
                {isSubmitting ? "Submitting..." : "Submit Inspection"}
              </button>
            </div>
          </div>

          {errors.responses && (
            <p className="text-red-500 text-sm mt-2">{errors.responses}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionForm;
